import { SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Input,
  Layout,
  Pagination,
  Space,
  Table,
} from "antd";
import { Content } from "antd/es/layout/layout";
import { ColumnType, TableRowSelection } from "antd/es/table/interface";
import React, { useEffect, useMemo, useState } from "react";
import Loader from "../../../Home/Loader/Loader";
import { connect } from "react-redux";
import { useNavigate, Link } from "react-router-dom";

interface IInventoryItemPayload {
  id: number;
  itemName: string;
  itemType: number;
  category: number;
  subCategory: number;
  microCategory: number;
  alternateUnit: number | null;
  conversionFactor: number | null;
  metricsUnit: number;
  HSNCode: string | null;
  price: number;
  taxType: number;
  tax: number;
  currentStock: number;
  minStock: number;
  maxStock: number;
  description: string | null;
  companyId: number;
  itemId: string;
  status: number;
  customFields: string;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface IOutOfStockExtraProps {
  loading: boolean;
  tableData:
  | IInventoryItemPayload[]
  | {
    data?: IInventoryItemPayload[];
    items?: IInventoryItemPayload[];
    total?: number;
  };
  UOMData: any;
  categoryData: any;
}

interface InventoryRowData extends IInventoryItemPayload {
  key: string | number;
  stockValue: number;
}

type SearchableColumnKey = "itemId" | "itemName" | "uom" | "stockValue";

const OutOfStockExtra: React.FC<IOutOfStockExtraProps> = ({
  loading,
  tableData,
  UOMData,
  categoryData,
}) => {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [globalSearchText, setGlobalSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [columnSearchText, setColumnSearchText] = useState<
    Record<SearchableColumnKey, string>
  >({
    itemId: "",
    itemName: "",
    uom: "",
    stockValue: "",
  });

  const uomCodeMap = useMemo(() => {
    if (!Array.isArray(UOMData)) return {};

    return UOMData.reduce((acc: Record<number, string>, curr: any) => {
      if (typeof curr?.id === "number") {
        acc[curr.id] = curr?.code || "";
      }
      return acc;
    }, {});
  }, [UOMData]);

  const resolvedCategoryData = useMemo(() => {
    if (Array.isArray(categoryData?.data?.data)) return categoryData.data.data;
    if (Array.isArray(categoryData?.data)) return categoryData.data;
    if (Array.isArray(categoryData)) return categoryData;
    return [];
  }, [categoryData]);

  const formatCurrency = (value: number) => {
    const formatted = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value || 0);
    return formatted.replace(/^(\D+)(.*)$/, "$1 $2");
  };

  const rawData = useMemo<IInventoryItemPayload[]>(() => {
    if (Array.isArray(tableData)) return tableData;
    if (Array.isArray(tableData?.data)) return tableData.data;
    if (Array.isArray(tableData?.items)) return tableData.items;
    return [];
  }, [tableData]);

  const mappedData: InventoryRowData[] = rawData.map(
    (item: IInventoryItemPayload, index: number) => {
      const quantity = Number(item.currentStock) || 0;
      const itemPrice = Number(item.price) || 0;

      return {
        ...item,
        key: item.id ?? index,
        itemId: String(item.itemId || ""),
        itemName: String(item.itemName || ""),
        currentStock: quantity,
        minStock: Number.isFinite(item.minStock) ? item.minStock : 0,
        maxStock: Number.isFinite(item.maxStock) ? item.maxStock : 0,
        stockValue: quantity * itemPrice,
      };
    },
  );

  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const availableKeys = new Set(mappedData.map((item) => item.itemId));
      return prev.filter((key) => availableKeys.has(String(key)));
    });
  }, [mappedData]);

  const getColumnValueForSearch = (
    record: InventoryRowData,
    columnKey: SearchableColumnKey,
  ): string => {
    if (columnKey === "itemId")
      return String(record.itemId || "").toLowerCase();
    if (columnKey === "itemName")
      return String(record.itemName || "").toLowerCase();
    if (columnKey === "uom")
      return String(uomCodeMap[record.metricsUnit] || "").toLowerCase();
    return String(record.stockValue ?? "").toLowerCase();
  };

  const globallyFilteredData = useMemo(() => {
    const normalizedSearch = globalSearchText.trim().toLowerCase();
    if (!normalizedSearch) return mappedData;

    return mappedData.filter((record) => {
      const searchableValues = [
        String(record.itemId || "").toLowerCase(),
        String(record.itemName || "").toLowerCase(),
        String(uomCodeMap[record.metricsUnit] || "").toLowerCase(),
        String(record.stockValue ?? "").toLowerCase(),
      ];

      return searchableValues.some((value) => value.includes(normalizedSearch));
    });
  }, [mappedData, globalSearchText, uomCodeMap]);

  const fullyFilteredData = useMemo(() => {
    return globallyFilteredData.filter((record) => {
      const searchableColumns = Object.keys(
        columnSearchText,
      ) as SearchableColumnKey[];

      return searchableColumns.every((columnKey) => {
        const searchText = columnSearchText[columnKey].trim().toLowerCase();
        if (!searchText) return true;
        return getColumnValueForSearch(record, columnKey).includes(searchText);
      });
    });
  }, [globallyFilteredData, columnSearchText]);

  const total = fullyFilteredData.length;

  const paginatedData = useMemo(() => {
    return fullyFilteredData.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );
  }, [fullyFilteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [mappedData.length, globalSearchText, columnSearchText]);

  const getColumnSearchProps = (
    columnKey: SearchableColumnKey,
    label: string,
  ): Partial<ColumnType<InventoryRowData>> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${label}`}
          value={(selectedKeys[0] as string) || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            const value = String(selectedKeys[0] || "").trim();
            setColumnSearchText((prev) => ({ ...prev, [columnKey]: value }));
            confirm();
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              const value = String(selectedKeys[0] || "").trim();
              setColumnSearchText((prev) => ({ ...prev, [columnKey]: value }));
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              setColumnSearchText((prev) => ({ ...prev, [columnKey]: "" }));
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: columnSearchText[columnKey]
      ? [columnSearchText[columnKey]]
      : null,
    onFilter: () => true,
  });

  const columns: ColumnType<InventoryRowData>[] = [
    {
      title: "Item ID",
      dataIndex: "itemId",
      key: "itemId",
      width: 150,
      fixed: "left",
      sorter: (a: any, b: any) =>
        String(a.itemId || "").localeCompare(
          String(b.itemId || ""),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          },
        ),
      ...getColumnSearchProps("itemId", "Item ID"),
    },
    {
      title: "Item Name",
      dataIndex: "itemName",
      key: "itemName",
      width: 260,
      sorter: (a: any, b: any) =>
        String(a.itemName || "").localeCompare(String(b.itemName || "")),
      render: (value: string) => (
        <span style={{ fontWeight: 500 }}>{value || "N/A"}</span>
      ),
      ...getColumnSearchProps("itemName", "Item Name"),
    },
    {
      title: "Current Stock",
      dataIndex: "currentQuantity",
      key: "currentQuantity",
      width: 180,
      sorter: (a: any, b: any) =>
        (Number(a.currentQuantity) || 0) - (Number(b.currentQuantity) || 0),
      render: (value: number, record: InventoryRowData) => (
        <span>
          {Number(value || 0).toFixed(2)} {uomCodeMap[record.metricsUnit] || ""}
        </span>
      ),
    },
    {
      title: "Min/Max Stock Data",
      dataIndex: "stockRange",
      key: "stockRange",
      width: 220,
      sorter: (a: any, b: any) =>
        (Number(a.currentStock) || 0) - (Number(b.currentStock) || 0),
      render: (_: any, record: InventoryRowData) => (
        <div>
          <div>Min Stock: {record.minStock ?? "N/A"}</div>
          <div>Max Stock: {record.maxStock ?? "N/A"}</div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 260,
      sorter: (a: any, b: any) => {
        const categoryA = resolvedCategoryData.find(
          (cat: any) => cat.id === a.category,
        );
        const categoryB = resolvedCategoryData.find(
          (cat: any) => cat.id === b.category,
        );
        const nameA = String(categoryA?.name || "");
        const nameB = String(categoryB?.name || "");
        return nameA.localeCompare(nameB);
      },
      render: (_: any, record: any) => {
        const category = resolvedCategoryData.find(
          (cat: any) => cat.id === record.category,
        );
        const subCategory = category?.child?.find(
          (sub: any) => sub.id === record.subCategory,
        );
        const microCategory = subCategory?.child?.find(
          (micro: any) => micro.id === record.microCategory,
        );

        const categoryRows = [
          { label: "Category", name: category?.name || "" },
          subCategory && {
            label: "Sub Category",
            name: subCategory?.name || "",
          },
          microCategory && {
            label: "Micro Category",
            name: microCategory?.name || "",
          },
        ].filter(Boolean) as Array<{ label: string; name: string }>;

        return (
          <>
            {categoryRows.length > 0 && categoryRows[0]?.name ? (
              categoryRows.map(({ label, name }, index) => (
                <div key={index}>
                  <span className="link">{label}: </span>
                  <span>{name}</span>
                </div>
              ))
            ) : (
              <span>N/A</span>
            )}
          </>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "metricsUnit",
      key: "uom",
      width: 180,
      sorter: (a: any, b: any) =>
        String(uomCodeMap[a.metricsUnit] || "").localeCompare(
          String(uomCodeMap[b.metricsUnit] || ""),
        ),
      render: (value: number) => <span>{uomCodeMap[value] || "N/A"}</span>,
      ...getColumnSearchProps("uom", "UOM"),
    },
  ];

  const rowSelection: TableRowSelection<InventoryRowData> = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onSelect: (record: InventoryRowData, selected: boolean) => {
      if (selected) {
        setSelectedRowKeys((prev) =>
          Array.from(new Set([...prev, record.itemId])),
        );
      } else {
        setSelectedRowKeys((prev) =>
          prev.filter((key) => key !== record.itemId),
        );
      }
    },
    onSelectMultiple: (
      selected: boolean,
      _selectedRows: InventoryRowData[],
      changeRows: InventoryRowData[],
    ) => {
      const changeKeys = changeRows.map((row) => row.itemId);
      if (selected) {
        setSelectedRowKeys((prev) =>
          Array.from(new Set([...prev, ...changeKeys])),
        );
      } else {
        setSelectedRowKeys((prev) =>
          prev.filter((key) => !changeKeys.includes(String(key))),
        );
      }
    },
    onSelectAll: (selected: boolean) => {
      if (selected) {
        const allKeys = fullyFilteredData.map((item) => item.itemId);
        setSelectedRowKeys(allKeys);
      } else {
        setSelectedRowKeys([]);
      }
    },
  };

  const purchaseRequestItems = useMemo(() => {
    const selectedKeySet = new Set(selectedRowKeys.map((key) => String(key)));
    return mappedData
      .filter((item) => selectedKeySet.has(item.itemId))
      .map((item) => ({
        itemId: item.itemId,
        itemName: item.itemName,
        quantity: Number(item.minStock) > 0 ? Number(item.minStock) : 1,
      }));
  }, [mappedData, selectedRowKeys]);

  const handleCreatePurchaseRequest = () => {
    navigate("/create_document?documentType=purchaseRequest", {
      state: {
        purchaseRequestItems,
      },
    });
  };

  const breadcrumbItems = [
    { key: "dashboard", title: <Link to="/dashboard">Dashboard</Link> },
    { key: "out-of-stock-items", title: "Out Of Stock Items" },
  ];

  return (
    <>
      <Loader loading={loading} />
      <Layout className="layout">
        <div className="flexBox mb-flexFlow" style={{ alignItems: "center" }}>
          <Breadcrumb className="breadcrumb" items={breadcrumbItems} />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined style={{ color: "#ffffff" }} />}
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
        </div>

        <Content className="content-section">
          <div className="mb-10">
            <div className="flexBox mb-10">
              <Input
                placeholder="Search"
                value={globalSearchText}
                onChange={(e) => setGlobalSearchText(e.target.value)}
                style={{ width: 250 }}
              />
              {selectedRowKeys?.length ? (
                <Button type="primary" onClick={handleCreatePurchaseRequest}>
                  Create Purchase Request
                </Button>
              ) : null}
            </div>
            <>
              <Table
                columns={columns}
                rowKey="itemId"
                dataSource={paginatedData}
                bordered
                pagination={false}
                rowSelection={rowSelection}
                scroll={{ x: "100%", y: "calc(100vh - 320px)" }}
              />

              <Pagination
                total={total}
                defaultPageSize={20}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showSizeChanger
                style={{ textAlign: "center", marginTop: 20 }}
              />
            </>
          </div>
        </Content>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  UOMData: state.api.getUOM?.data,
  categoryData: state.api?.getCategory,
});

export default connect(mapStateToProps)(OutOfStockExtra);
